import { useMemo } from 'react'
import { TransitOperation } from '../../transit-operation/models/types'
import useSaveDeclarationForm from './useSaveDeclarationForm'
import { Consignment } from '../../consignment/models/types'
import { useFetchGuarantees } from '../../guarantee/api/fetchGuarantees'
import { useFetchAuthorisations } from '../../authorisation/api/fetchAuthorisations'
import { Guarantee } from '../../guarantee/models/types'
import { Authorisation } from '../../authorisation/models/types'
import { useFetchConsignmentDocuments } from '../../consignment-document/api/fetchConsignmentDocuments'
import useValidateDeclaration from './useValidateDeclaration'
import { Declaration } from '../../models/types'
import { useDeclarationEditorContext } from '../contexts/DeclarationEditorContext'
import { Document } from '../../document/models/types'

const createDeclaration = (
  transitOperation: TransitOperation | undefined,
  consignment: Consignment | undefined,
  consignmentDocuments: Document[] | undefined,
  guarantees: Guarantee[] | undefined,
  authorisations: Authorisation[] | undefined,
): Declaration => ({
  transitOperation,
  consignment,
  consignmentDocuments: consignmentDocuments ?? [],
  guarantees: guarantees ?? [],
  authorisations: authorisations ?? [],
})

function useDeclaration() {
  const { declarationData, isLoading: isDeclarationLoading } = useDeclarationEditorContext()
  const { transitOperation, consignment } = declarationData

  const transitOperationId = transitOperation?.id ?? null
  const { data: consignmentDocuments, isFetching: isConsignmentDocumentsLoading } = useFetchConsignmentDocuments(consignment?.id ?? null)
  const { data: guarantees, isFetching: isGuaranteesLoading } = useFetchGuarantees(transitOperationId)
  const { data: authorisations, isFetching: isAuthorisationsLoading } = useFetchAuthorisations(transitOperationId)

  const declaration = useMemo(
    () => createDeclaration(transitOperation, consignment, consignmentDocuments, guarantees, authorisations),
    [transitOperation, consignment, consignmentDocuments, guarantees, authorisations],
  )

  const { saveDeclaration, isSubmitting } = useSaveDeclarationForm({ declaration })
  const { validateDeclaration } = useValidateDeclaration(transitOperationId)

  const isLoading = isDeclarationLoading
    || isConsignmentDocumentsLoading || isGuaranteesLoading || isAuthorisationsLoading

  return {
    declaration,
    saveDeclaration,
    validateDeclaration,
    isLoading,
    isSubmitting,
  }
}

export default useDeclaration
